import React from "react";
import { ArrowUpRight, ArrowRight, Briefcase, Home as HomeIcon, Store, Utensils, ShieldCheck, Sparkles } from "lucide-react";
import { useModals } from "@/lib/modal-context";

const WhatsIcon = ({ className = "w-5 h-5" }) => (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
        <path d="M19.05 4.91A9.83 9.83 0 0 0 12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.74.46 3.45 1.32 4.95L2 22l5.25-1.38a9.86 9.86 0 0 0 4.79 1.22h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.91-7.02Z"/>
    </svg>
);

// Custom assets provided by client
const HERO_IMG = "https://customer-assets.emergentagent.com/job_dream-design-deliver/artifacts/c6d5zg1i_Main.png";
const OFFICE_IMG = "https://customer-assets.emergentagent.com/job_dream-design-deliver/artifacts/83t8nwft_Fitout.jpg";
const HOME_IMG = "https://customer-assets.emergentagent.com/job_dream-design-deliver/artifacts/rhhmp5k8_Home.jpg";
const RETAIL_IMG = "https://customer-assets.emergentagent.com/job_dream-design-deliver/artifacts/sc7pkfad_Retail.jpg";

function CardImage({ image, label, sub, big = false, to = "/", testid, icon }) {
    return (
        <div data-testid={testid} className={`relative overflow-hidden rounded-[24px] card-lift ${big ? "h-full min-h-[220px]" : "h-full min-h-[200px]"}`}>
            <img src={image} alt={label} className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
            <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/70 via-black/25 to-transparent" />
            {icon && <div className="absolute left-4 top-4 flex h-9 w-9 items-center justify-center rounded-full bg-white/95">{icon}</div>}
            <div className="relative z-10 flex h-full flex-col justify-end p-5 sm:p-6">
                <div className="flex items-end justify-between gap-3">
                    <div className="text-white">
                        <div className="font-display text-[22px] sm:text-[26px] font-bold leading-[1.05] tracking-tight">{label}</div>
                        <div className="mt-1.5 text-[13px] text-white/95 leading-snug max-w-[14rem]">{sub}</div>
                    </div>
                    <button className="circle-arrow shrink-0 h-10 w-10" aria-label="explore">
                        <ArrowUpRight className="h-4 w-4" />
                    </button>
                </div>
            </div>
        </div>
    );
}

const TRUST = [
    { label: "45-Day Delivery Plan" },
    { label: "Transparent Pricing" },
    { label: "Premium Materials" },
    { label: "Office + Home + Retail" },
    { label: "One Team. Zero Headache." },
    { label: "Free Design Consultation" },
];

export default function HeroMain() {
    const { openConnect, openEstimator } = useModals();

    return (
        <section id="home" className="bg-[#FAFAF5]">
            <div className="mx-auto max-w-[1440px] px-4 sm:px-6 lg:px-8 pt-5 pb-10">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                    {/* LEFT — office fit-out hero */}
                    <div className="lg:col-span-7 relative overflow-hidden rounded-[28px] min-h-[460px] lg:min-h-[580px]">
                        <img src={HERO_IMG} alt="Premium office fit-out in Dubai" className="absolute inset-0 h-full w-full object-cover" />
                        <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(110deg, rgba(250,250,245,0.94) 0%, rgba(250,250,245,0.72) 30%, rgba(250,250,245,0.25) 52%, rgba(250,250,245,0) 70%)" }} />
                        <div className="relative z-10 flex h-full flex-col justify-between p-7 sm:p-9 lg:p-11">
                            <div className="max-w-[38rem] fade-up">
                                <div className="inline-flex items-center gap-2 rounded-full bg-white/70 backdrop-blur px-3 py-1 text-[10px] font-bold tracking-[0.15em] text-black/80 border border-black/10">
                                    <span className="h-1.5 w-1.5 rounded-full bg-[#7e9a00]" />
                                    OFFICE • HOME • RETAIL • RESTAURANT
                                </div>
                                <h1 className="mt-4 font-display text-[34px] leading-[1.08] sm:text-[42px] lg:text-[48px] font-extrabold text-[#111] tracking-[-0.03em]">
                                    Dubai Interiors &amp; Fit-Out<br />
                                    <span className="whitespace-nowrap">
                                        Delivered in{" "}
                                        <span className="inline-block bg-[#CCFF00] px-2 pt-0.5 pb-0.5 rounded-md">45 Days</span>
                                    </span>
                                </h1>
                                <p className="mt-5 text-[14px] text-[#3a3a3a] max-w-[28rem] leading-[1.6] font-medium">
                                    From office fit-outs and retail branding to home interiors and restaurant spaces — 45DaySpaces designs and delivers premium spaces with speed, clarity and zero headache.
                                </p>
                            </div>
                            <div className="mt-8 flex flex-wrap gap-3">
                                <button onClick={() => openEstimator("office")} data-testid="hero-main-estimate" className="btn-pill btn-lime pl-5 pr-1 py-1.5">
                                    <span className="text-[12px] font-bold tracking-[0.1em]">GET FREE ESTIMATE</span>
                                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-black text-[#CCFF00]"><ArrowRight className="h-3.5 w-3.5" /></span>
                                </button>
                                <button onClick={() => openConnect()} data-testid="hero-main-connect" className="btn-pill btn-outline-dark pl-5 pr-1 py-1.5">
                                    <span className="text-[12px] font-bold tracking-[0.1em]">CONNECT IN 15 SECS</span>
                                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-black text-[#CCFF00]"><WhatsIcon className="h-3.5 w-3.5" /></span>
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* RIGHT — 3 category cards */}
                    <div className="lg:col-span-5 grid grid-cols-2 grid-rows-[auto_auto] gap-4 min-h-[460px] lg:min-h-[580px]">
                        <CardImage image={OFFICE_IMG} label="Office Fit-Out" sub="Modern workspaces, delivered fast" testid="hero-main-card-office" icon={<Briefcase className="h-4 w-4 text-black" />} />
                        <CardImage image={HOME_IMG} label="Home Interiors" sub="Apartments and villas, end-to-end" testid="hero-main-card-home" icon={<HomeIcon className="h-4 w-4 text-black" />} />
                        <div className="col-span-2">
                            <CardImage image={RETAIL_IMG} label="Retail & Restaurant Spaces" sub="Storefronts, cafés and dining that convert" big testid="hero-main-card-retail" icon={<Store className="h-4 w-4 text-black" />} />
                        </div>
                    </div>
                </div>

                {/* Trust strip */}
                <div className="mt-5 rounded-[24px] border border-black/10 bg-white px-5 py-3">
                    <div className="flex flex-wrap gap-x-6 gap-y-2 items-center justify-between">
                        {TRUST.map((t) => (
                            <div key={t.label} className="inline-flex items-center gap-2 text-[12px] font-bold text-black/80">
                                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#CCFF00]"><ShieldCheck className="h-3 w-3 text-black" /></span>
                                {t.label}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}
